import { canv } from "@/canv/global.js";
import { getTarget } from "@/canv/utils.js";
import type { widgetOption } from "@/widgetMenu/widgetMenu";
import { wmBase } from "@/widgetMenu/wmBase";

export interface BorderOptions {
    borderColor?: string;
    borderWidth?: number;
    borderDashType?: "solid" | "dashed" | "dotted";
}

export type BorderOption = widgetOption<BorderOptions>;

export class wmBorder extends wmBase {
    public options: BorderOption[];
    public colors: string[] = [
        "transparent",
        "rgb(17, 17, 17)",
        "rgb(117, 117, 117)",
        "rgb(224, 62, 26)",
        "rgb(247, 151, 34)",
        "rgb(243, 193, 27)",
        "rgb(25, 143, 81)",
        "rgb(12, 140, 233)",
        "rgb(138, 56, 245)",
        "rgb(255, 255, 255)",
    ];
    public dashTypes: Record<"solid" | "dashed" | "dotted", string> = {
        solid: `
      <svg width="23" height="23" viewBox="0 0 104 7" fill="none" xmlns="http://www.w3.org/2000/svg">
        <line y1="3.5" x2="104" y2="3.5" stroke="black" stroke-width="7"/>
      </svg>
    `,
        dashed: `
      <svg width="23" height="23" viewBox="0 0 104 7" fill="none" xmlns="http://www.w3.org/2000/svg">
        <line y1="3.5" x2="104" y2="3.5" stroke="black" stroke-width="7" stroke-dasharray="14 14"/>
      </svg>
    `,
        dotted: `
      <svg width="23" height="23" viewBox="0 0 104 7" fill="none" xmlns="http://www.w3.org/2000/svg">
        <line x1="3.5" y1="3.5" x2="100.5" y2="3.5" stroke="black" stroke-width="7" stroke-linecap="round" stroke-dasharray="0.1 15"/>
      </svg>
    `,
    };

    public currentBorderColor: string = this.colors[0];
    public currentBorderWidth = 1;
    public currentBorderDashType: "solid" | "dashed" | "dotted" = "solid";

    constructor(options) {
        super();
        this.options = options;
        const first = options?.[0]?.value;
        if (first) {
            this.currentBorderColor = first?.borderColor ?? this.currentBorderColor;
            this.currentBorderWidth = first?.borderWidth ?? this.currentBorderWidth;
            this.currentBorderDashType = first?.borderDashType ?? this.currentBorderDashType;
        }
    }

    public build(): string {
        const color = this.currentBorderColor;
        const innerClass = color === "transparent" ? "wm_border_inner_clear" : "";
        const style = color === "transparent" ? "" : `style="border:1px solid ${color}"`;

        return `
            <div class="wm_border">
                <div class="wm_border_inner ${innerClass}" ${style}></div>
            </div>
        `;
    }

    public setOnlyIniq(color: string): void {
        const innerClass = color === "transparent" ? "wm_border_inner_clear" : "";
        const style = color === "transparent" ? "" : `style="border:1px solid ${color}"`;
        const template = `<div class="wm_border_inner ${innerClass}" ${style}></div>`;

        const item = document.getElementsByClassName("wm_border")[0] as HTMLElement | undefined;
        if (item) {
            item.innerHTML = template;
        }
    }

    public init(): void {
        const item = document.getElementsByClassName("wm_border")[0] as HTMLElement | undefined;
        if (item) {
            item.removeEventListener("mousedown", this.toggleSubMenu.bind(this), false);
            item.addEventListener("mousedown", this.toggleSubMenu.bind(this), false);
        }
    }

    private toggleSubMenu(e: MouseEvent): void {
        const existing = document.getElementsByClassName("wm_border_submenu")[0];
        if (existing) {
            existing.outerHTML = "";
            return;
        }

        const menuExists = document.getElementsByClassName("widgetSubMenu")[0];
        if (menuExists) {
            menuExists.outerHTML = "";
        }

        canv.widgetMenu.subMenuBoss = "wm_border";
        const submenu = document.createElement("div");
        submenu.classList.add("widgetSubMenu", "wm_border_submenu");
        submenu.style.opacity = "0";
        document.body.prepend(submenu);

        submenu.innerHTML += this.getSubMenuDashTypesDiv();
        submenu.innerHTML += `<input type="range" class="wm_border_range" value="${this.currentBorderWidth}" min="1" max="30"/>`;
        submenu.innerHTML += this.getSubMenuColorsDiv();

        Array.from(submenu.getElementsByClassName("wm_border_submenu_dash_item")).forEach((el) => {
            const element = el as HTMLElement;
            element.removeEventListener("mouseup", this.dashChange.bind(this));
            element.addEventListener("mouseup", this.dashChange.bind(this));
        });
        Array.from(submenu.getElementsByClassName("wm_border_submenu_colors_item")).forEach(
            (el) => {
                const element = el as HTMLElement;
                element.removeEventListener("mouseup", this.itemClick.bind(this));
                element.addEventListener("mouseup", this.itemClick.bind(this));
            },
        );
        submenu
            .getElementsByClassName("wm_border_range")[0]
            ?.addEventListener("input", this.rangeChange.bind(this));

        setTimeout(canv.widgetMenu.TryLocateMenu.bind(canv.widgetMenu), 50);
    }

    private itemClick(e: MouseEvent): void {
        const item = getTarget(e, "wm_border_submenu_colors_item") as HTMLElement;
        const num = Number(item.getAttribute("num"));
        const color = this.colors[num];
        this.applyValue(this.options, { borderColor: color }, false);
        this.currentBorderColor = color;
        this.setOnlyIniq(color);

        Array.from(document.getElementsByClassName("wm_border_submenu_colors_item")).forEach((el) =>
            el.classList.remove("wm_border_submenu_selected"),
        );
        item.classList.add("wm_border_submenu_selected");
    }

    private getSubMenuDashTypesDiv(): string {
        const sel = (type: "solid" | "dashed" | "dotted") =>
            type === this.currentBorderDashType ? "wm_border_submenu_selected" : "";
        return `
      <div class="wm_border_submenu_dash">
        <div class="wm_border_submenu_dash_item ${sel("solid")}" type="solid">${this.dashTypes.solid}</div>
        <div class="wm_border_submenu_dash_item ${sel("dashed")}" type="dashed">${this.dashTypes.dashed}</div>
        <div class="wm_border_submenu_dash_item ${sel("dotted")}" type="dotted">${this.dashTypes.dotted}</div>
      </div>
    `;
    }

    private getSubMenuColorsDiv(): string {
        let template = `<div class="wm_border_submenu_colors">`;
        this.colors.forEach((color, i) => {
            const isClear = i === 0;
            const selected = color === this.currentBorderColor ? "wm_border_submenu_selected" : "";
            const cls = `wm_border_submenu_colors_item${isClear ? "_clear" : ""} ${selected}`;
            // biome-ignore lint/style/noUnusedTemplateLiteral: <explanation>
            const inner = isClear ? `<div></div>` : `<div style="background:${color}"></div>`;
            template += `<div class="${cls}" num="${i}">${inner}</div>`;
        });
        template += "</div>";
        return template;
    }

    private rangeChange(e: Event): void {
        const target = getTarget(e, "wm_border_range") as HTMLInputElement;
        const width = Number.parseInt(target.value, 10);
        this.applyValue(this.options, { borderWidth: width }, false);
        this.currentBorderWidth = width;
    }

    private dashChange(e: MouseEvent): void {
        const item = getTarget(e, "wm_border_submenu_dash_item") as HTMLElement;
        const type = item.getAttribute("type") as "solid" | "dashed" | "dotted";
        this.applyValue(this.options, { borderDashType: type }, false);
        this.currentBorderDashType = type;

        Array.from(document.getElementsByClassName("wm_border_submenu_dash_item")).forEach((el) =>
            el.classList.remove("wm_border_submenu_selected"),
        );
        item.classList.add("wm_border_submenu_selected");
    }

    public name = "wm_border";
}
